import React, { useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import Home from './customer/home';
import Login from './customer/login';
import MFA from './customer/mfa';
import Leads from './customer/leadsnew';
import Advert from './customer/advertnew';
import Gallery from './customer/gallery';
import LeadDoc from './customer/leaddoc';
import LoadingComponent from './customer/loading';
import ForgotPassword from './customer/forgotpassword';
import Logout from './customer/logout';
import QualificationForm from './customer/qualificationform';
import QualificationPassword from './customer/qualificationpassword';
import Billing from './customer/billing';
import Unsubscribe from './customer/unsubscribe';
import Tos from './customer/tos';
import TosStatic from './customer/TosStatic';
import AvenorJobProgress from './customer/avenorjobprogress';
import CompanySettingsModal from './customer/components/CompanySettingsModal';
import ICP from './customer/components/ICP';
import ServiceUnavailable from './customer/404';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.insightos.co.uk';
const CONNECTIVITY_CHECK_INTERVAL_MS = 10000;

// Polls the backend/db health endpoint; swaps the whole app out for the
// ServiceUnavailable page while unreachable, which itself redirects back to
// /leads once connectivity (and, via ProtectedRoute, auth) is confirmed again.
function ConnectivityGate({ children }) {
  const [isServiceDown, setIsServiceDown] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkConnectivity = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/db-connection`, { credentials: 'include' });
        const data = await response.json().catch(() => null);
        if (!cancelled) {
          setIsServiceDown(!(response.ok && data?.success));
        }
      } catch {
        if (!cancelled) setIsServiceDown(true);
      }
    };

    checkConnectivity();
    const intervalId = setInterval(checkConnectivity, CONNECTIVITY_CHECK_INTERVAL_MS);

    const handleOffline = () => setIsServiceDown(true);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', checkConnectivity);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', checkConnectivity);
    };
  }, []);

  if (isServiceDown) {
    return <ServiceUnavailable />;
  }

  return children;
}

function ProtectedRoute({ children }) {
  const auth = useContext(AuthContext);

  if (auth?.isVerifying) {
    return <LoadingComponent />;
  }

  if (!auth?.isAuthenticated) {
    return <Navigate to="/leads/login" replace />;
  }

  return children;
}

function PublicOnlyRoute({ children }) {
  const auth = useContext(AuthContext);

  if (auth?.isVerifying) {
    return <LoadingComponent />;
  }

  if (auth?.isAuthenticated) {
    return <Navigate to="/leads" replace />;
  }

  return children;
}


function App() {
  return (
    <ConnectivityGate>
      <Routes>
        <Route path="/" element={<Home />} />
      <Route
        path="/login"
        element={(
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        )}
      />
      <Route
        path="/leads/login"
        element={(
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        )}
      />
      <Route
        path="/mfa"
        element={(
          <PublicOnlyRoute>
            <MFA />
          </PublicOnlyRoute>
        )}
      />
      <Route
        path="/leads/mfa"
        element={(
          <PublicOnlyRoute>
            <MFA />
          </PublicOnlyRoute>
        )}
      />
      <Route
        path="/forgotpassword"
        element={(
          <PublicOnlyRoute>
            <ForgotPassword />
          </PublicOnlyRoute>
        )}
      />
      <Route
        path="/leads/forgotpassword"
        element={(
          <PublicOnlyRoute>
            <ForgotPassword />
          </PublicOnlyRoute>
        )}
      />
      <Route path="/logout" element={<Logout />} />
      <Route path="/leads/logout" element={<Logout />} />
      <Route path="/avenorloading/leads/:jobId" element={<AvenorJobProgress queueType="leads" />} />
          <Route path="/avenorloading/advert/:jobId" element={<AvenorJobProgress queueType="advert" />} />
      <Route path="/qualification" element={<QualificationForm />} />
      <Route path="/tos" element={<Tos />} />
      <Route path="/tosstatic" element={<TosStatic />} />
      <Route path="/qualification/password" element={<QualificationPassword />} />
      <Route path="/unsubscribe/:id" element={<Unsubscribe />} />
      <Route
        path="/leads"
        element={(
          <ProtectedRoute>
            <Leads />
          </ProtectedRoute>
        )}
      />
      

      <Route
        path="/settings"
        element={(
          <ProtectedRoute>
            <CompanySettingsModal />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/research"
        element={(
          <ProtectedRoute>
            <ICP />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/adverts"
        element={(
          <ProtectedRoute>
            <Advert />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/gallery"
        element={(
          <ProtectedRoute>
            <Gallery />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/billing"
        element={(
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        )}
      />
      <Route
        path="/leads-docs"
        element={(
          <ProtectedRoute>
            <LeadDoc />
          </ProtectedRoute>
        )}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ConnectivityGate>
  );
}

export default App;
